import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { Button, Dialog, DialogContent, LinearProgress, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MovieIcon from "@mui/icons-material/Movie";
import axios from "axios";
import agent from "../api/agent";
import AdminLayout from "../layout/AdminLayout";
import { VideoGalleryItem } from "../models/videoGalleryItem";
import { env } from "../utils/env";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#f6f6f6",
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
  height: "100%",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function VideoGallery() {
  const [videos, setVideos] = useState<VideoGalleryItem[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    const response = await agent.VideoGalleryItems.list();
    setVideos(response);
  };

  const resolveAssetUrl = (value?: string | null) =>
    value ? (value.startsWith("http") ? value : env + value) : "";

  const handleDelete = async (id: string) => {
    await agent.VideoGalleryItems.delete(id);
    if (selectedId === id) {
      setSelectedId(null);
    }
    fetchVideos();
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    event.target.value = "";

    try {
      setUploadProgress(0);
      setUploadOpen(true);

      await axios.post("/videoGalleryItems", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent: any) => {
          const total = progressEvent?.total ?? file.size ?? 0;
          const loaded = progressEvent?.loaded ?? 0;
          if (!total) return;
          const percent = Math.round((loaded / total) * 100);
          setUploadProgress(Math.min(100, Math.max(0, percent)));
        },
      });

      setUploadProgress(100);
      await fetchVideos();
    } catch (error) {
      console.error("There was an error uploading the video:", error);
    } finally {
      setTimeout(() => {
        setUploadOpen(false);
        setUploadProgress(0);
      }, 300);
    }
  };

  const getDisplayName = (video: VideoGalleryItem) => {
    if (video.fileName) return video.fileName;
    const clean = (video.videoUrl || "").split(/[?#]/)[0];
    const parts = clean.split("/");
    return parts.pop() || "video.mp4";
  };

  return (
    <AdminLayout title="Video Galerisi">
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
            >
              Video Yükle
              <VisuallyHiddenInput
                type="file"
                accept=".mp4,video/mp4"
                onChange={handleUpload}
              />
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={4} sx={{ mt: 1 }}>
          {videos.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
              <Item
                onClick={() => setSelectedId(video.id)}
                sx={{
                  cursor: "pointer",
                  border: selectedId === video.id ? "2px solid #1976d2" : "2px solid transparent",
                  transition: "border-color 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mb: 2,
                    color: "#545454",
                  }}
                >
                  <MovieIcon />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Video
                  </Typography>
                </Box>

                <video
                  controls
                  preload="metadata"
                  poster={resolveAssetUrl(video.videoThumb)}
                  style={{ width: "100%", borderRadius: 6, marginBottom: 12 }}
                  src={resolveAssetUrl(video.videoUrl)}
                />

                <Typography
                  variant="body2"
                  sx={{
                    minHeight: 40,
                    fontWeight: 600,
                    overflowWrap: "anywhere",
                    mb: 2,
                  }}
                >
                  {getDisplayName(video)}
                </Typography>

                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(video.id);
                  }}
                >
                  Sil
                </Button>
              </Item>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog open={uploadOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Yükleniyor... {uploadProgress}%
          </Typography>

          <LinearProgress variant="determinate" value={uploadProgress} />

          <Typography variant="caption" sx={{ mt: 1, display: "block", opacity: 0.7 }}>
            Video yüklemesi tamamlanana kadar bekleyiniz.
          </Typography>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
